import {
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Card,
  CardContent,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Add, Delete, CloudUpload, DescriptionOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {
  BRAND,
  requiredFieldSx,
  subsectionHeadingSx,
  sectionSubheadingSx,
  entryCardSx,
  uploadBoxSx,
  addEntryButtonSx,
  requiredAsteriskSx,
  ALLOWED_FILE_ACCEPT,
  FILE_UPLOAD_HELPER_TEXT,
  validateDocumentFile,
  isExperienceEntryFilled,
  datePickerDesktopModeMediaQuery,
  datePickerLocaleText,
  getDatePickerSlotProps,
} from "../onboardingTheme";

const emptyEducation = {
  name: "",
  percentage: "",
  passingYear: "",
  doc: null,
};

const emptyExperience = {
  company: "",
  joining: "",
  end: "",
  designation: "",
  doc: null,
};

const EducationExperienceStep = ({
  values,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  touched,
  errors,
}) => {
  const handleAdd = (key, emptyTemplate) => {
    setFieldValue(key, [...values[key], { ...emptyTemplate }]);
  };

  const handleRemove = (key, index) => {
    const updated = [...values[key]];
    updated.splice(index, 1);
    setFieldValue(key, updated);
  };

  const handleFieldChange = (key, index, field, value) => {
    const updated = [...values[key]];
    updated[index] = { ...updated[index], [field]: value };
    setFieldValue(key, updated);
  };

  const handleDocumentSelect = (key, index, file) => {
    if (!file) return;
    const result = validateDocumentFile(file);
    if (!result.valid) {
      toast.error(result.message);
      return;
    }
    handleFieldChange(key, index, "doc", file);
    setFieldTouched(`${key}[${index}].doc`, true, false);
  };

  const UploadBox = ({
    label,
    onChange,
    file,
    error,
    helperText,
    required = false,
  }) => (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: BRAND.muted, mb: 0.5, display: "block", fontWeight: 500 }}
      >
        {label}
        {required && (
          <Box component="span" sx={requiredAsteriskSx}>
            *
          </Box>
        )}
      </Typography>
      <Box component="label" sx={uploadBoxSx(error)}>
        <CloudUpload sx={{ color: error ? "#EF4444" : BRAND.orange, fontSize: 24 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: error ? "#EF4444" : BRAND.navy,
              fontWeight: 500,
              fontSize: "0.8125rem",
            }}
          >
            {file ? file.name : "Click to browse"}
          </Typography>
          <Typography variant="caption" sx={{ color: BRAND.muted, display: "block" }}>
            {FILE_UPLOAD_HELPER_TEXT}
          </Typography>
        </Box>
        <input
          type="file"
          hidden
          accept={ALLOWED_FILE_ACCEPT}
          onChange={onChange}
        />
      </Box>
      {error && helperText && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box width="100%">
      <Typography sx={subsectionHeadingSx}>Education</Typography>
      <Typography sx={{ ...sectionSubheadingSx, mb: 1.5 }}>
        Add your academic qualifications and supporting documents.
      </Typography>

      {values.education.map((edu, index) => {
        const eduErrors = errors.education?.[index] || {};
        const eduTouched = touched.education?.[index] || {};

        return (
          <Card key={index} variant="outlined" sx={entryCardSx}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DescriptionOutlined sx={{ color: BRAND.orange, fontSize: 18 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ color: BRAND.navy, fontWeight: 600, fontSize: "0.8125rem" }}
                  >
                    Education {index + 1}
                  </Typography>
                </Box>
                {values.education.length > 1 && (
                  <IconButton
                    onClick={() => handleRemove("education", index)}
                    size="small"
                    aria-label="Remove education entry"
                    sx={{
                      color: "#EF4444",
                      bgcolor: "#FEE2E2",
                      "&:hover": { bgcolor: "#FECACA" },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="College / Institute Name"
                    value={edu.name || ""}
                    onChange={(e) =>
                      handleFieldChange("education", index, "name", e.target.value)
                    }
                    onBlur={handleBlur}
                    sx={requiredFieldSx}
                    error={Boolean(eduTouched.name && eduErrors.name)}
                    helperText={eduTouched.name && eduErrors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Percentage / CGPA"
                    value={edu.percentage || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "education",
                        index,
                        "percentage",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    sx={requiredFieldSx}
                    error={Boolean(eduTouched.percentage && eduErrors.percentage)}
                    helperText={eduTouched.percentage && eduErrors.percentage}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Passing Year"
                    value={edu.passingYear || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "education",
                        index,
                        "passingYear",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    sx={requiredFieldSx}
                    inputProps={{ maxLength: 4, inputMode: "numeric" }}
                    error={Boolean(eduTouched.passingYear && eduErrors.passingYear)}
                    helperText={eduTouched.passingYear && eduErrors.passingYear}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UploadBox
                    label="Upload Education Document"
                    required
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      handleDocumentSelect("education", index, file);
                    }}
                    file={edu.doc}
                    error={eduTouched.doc && eduErrors.doc}
                    helperText={eduErrors.doc}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" }, mb: 2 }}>
        <Button
          startIcon={<Add />}
          onClick={() => handleAdd("education", emptyEducation)}
          variant="outlined"
          size="small"
          sx={addEntryButtonSx}
        >
          Add Education
        </Button>
      </Box>

      <Typography sx={subsectionHeadingSx}>Work Experience</Typography>
      <Typography sx={{ ...sectionSubheadingSx, mb: 1.5 }}>
        Share your previous employment history (optional).
      </Typography>

      {values.experience.map((exp, index) => {
        const expErrors = errors.experience?.[index] || {};
        const expTouched = touched.experience?.[index] || {};
        const isActive = isExperienceEntryFilled(exp);

        return (
          <Card key={index} variant="outlined" sx={entryCardSx}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DescriptionOutlined sx={{ color: BRAND.orange, fontSize: 18 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ color: BRAND.navy, fontWeight: 600, fontSize: "0.8125rem" }}
                  >
                    Experience {index + 1}
                  </Typography>
                </Box>
                <IconButton
                    onClick={() => handleRemove("experience", index)}
                    size="small"
                    aria-label="Remove experience entry"
                    sx={{
                      color: "#EF4444",
                      bgcolor: "#FEE2E2",
                      "&:hover": { bgcolor: "#FECACA" },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
              </Box>

              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Company Name"
                    value={exp.company || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "company",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    sx={requiredFieldSx}
                    error={Boolean(isActive && expTouched.company && expErrors.company)}
                    helperText={isActive && expTouched.company && expErrors.company}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Designation"
                    value={exp.designation || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "designation",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    sx={requiredFieldSx}
                    error={Boolean(isActive && expTouched.designation && expErrors.designation)}
                    helperText={isActive && expTouched.designation && expErrors.designation}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Joining Date"
                    value={exp.joining ? dayjs(exp.joining) : null}
                    maxDate={exp.end ? dayjs(exp.end) : dayjs()}
                    desktopModeMediaQuery={datePickerDesktopModeMediaQuery}
                    localeText={datePickerLocaleText}
                    onChange={(date) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "joining",
                        date ? dayjs(date).format("YYYY-MM-DD") : ""
                      )
                    }
                    onClose={() =>
                      setFieldTouched(`experience[${index}].joining`, true, false)
                    }
                    slotProps={getDatePickerSlotProps({
                      error: Boolean(
                        isActive && expTouched.joining && expErrors.joining
                      ),
                      helperText:
                        isActive && expTouched.joining && expErrors.joining,
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="End Date"
                    value={exp.end ? dayjs(exp.end) : null}
                    minDate={exp.joining ? dayjs(exp.joining) : undefined}
                    maxDate={dayjs()}
                    desktopModeMediaQuery={datePickerDesktopModeMediaQuery}
                    localeText={datePickerLocaleText}
                    onChange={(date) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "end",
                        date ? dayjs(date).format("YYYY-MM-DD") : ""
                      )
                    }
                    onClose={() =>
                      setFieldTouched(`experience[${index}].end`, true, false)
                    }
                    slotProps={getDatePickerSlotProps({
                      error: Boolean(isActive && expTouched.end && expErrors.end),
                      helperText: isActive && expTouched.end && expErrors.end,
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <UploadBox
                    label="Upload Experience Document"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      handleDocumentSelect("experience", index, file);
                    }}
                    file={exp.doc}
                    error={isActive && expTouched.doc && expErrors.doc}
                    helperText={isActive && expErrors.doc}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
        <Button
          startIcon={<Add />}
          onClick={() => handleAdd("experience", emptyExperience)}
          variant="outlined"
          size="small"
          sx={addEntryButtonSx}
        >
          Add Experience
        </Button>
      </Box>
    </Box>
  );
};

export default EducationExperienceStep;
